import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import api, { formatDateTime, formatPhone } from '../services/api';
import AssignStylistModal from '../components/AssignStylistModal';
import BookingModal from '../components/BookingModal';
import ChatModal from '../components/ChatModal';
import RecordingModal from '../components/RecordingModal';
import {
  Confirm,
  ChipRow,
  DateRange,
  DetailRow,
  DownloadIcon,
  Loader,
  Modal,
  NoShowTag,
  Pagination,
  RefreshIcon,
  SearchBox,
  SourceBadge,
  Spinner,
  UnassignedPill,
  ViewButton,
  WelcomeBar,
  exportCSV,
  inDateRange,
  usePagination,
} from '../components/ui';

const STATUS_CHIPS = [
  { key: '', label: 'All' },
  { key: 'booked', label: 'Booked' },
  { key: 'service_taken', label: 'Service taken' },
  { key: 'no_show', label: 'No-show' },
];
const SOURCE_CHIPS = [
  { key: '', label: 'All' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'voice', label: 'Voice' },
  { key: 'walk_in', label: 'Walk-in / desk' },
];
const STYLIST_CHIPS = [
  { key: '', label: 'All' },
  { key: 'assigned', label: 'With stylist' },
  { key: 'unassigned', label: 'Unassigned' },
];
const STATUS_LABEL = { booked: 'Booked', service_taken: 'Service taken', no_show: 'No-show' };

/** What happens on WhatsApp when the status changes. */
function statusEffect(from, to) {
  const lines = [];
  if (to === 'service_taken') {
    lines.push('The feedback message is sent now and the return-visit reminders are scheduled from today.');
    if (from === 'booked') lines.push('Appointment reminders still waiting are cancelled.');
  }
  if (to === 'no_show') {
    if (from === 'booked') lines.push('Appointment reminders still waiting are cancelled.');
    lines.push(
      'The missed-appointment message goes out later, asking if they would like a new time (if it is turned on and the appointment is from the last 2 days). It stops if they reply or book.',
    );
  }
  if (from === 'no_show') lines.push('Missed-appointment messages not yet sent are cancelled.');
  if (to === 'booked') lines.push('Appointment reminders that are still ahead are scheduled again. No second confirmation is sent.');
  if (from === 'service_taken') {
    lines.push('Return-visit messages not yet sent are cancelled. A feedback message already sent cannot be taken back.');
  }
  return lines.join(' ');
}

/** "Did they come?": appointments still Booked well after their time, with one-tap answers. */
function NeedsUpdate({ rows, busy, onMark }) {
  const [showAll, setShowAll] = useState(false);
  if (!rows.length) return null;
  const shown = showAll ? rows : rows.slice(0, 5);
  return (
    <div className="nye-needs-update">
      <div className="nye-row nye-row--between nye-row--wrap" style={{ gap: 8 }}>
        <div className="nye-strong">
          🕒 Did they come? <span className="nye-pill nye-pill--warn nye-pill--sm">{rows.length}</span>
        </div>
        <div className="nye-small nye-muted">Still Booked after their time. Mark each one so reminders and follow-up messages stay right.</div>
      </div>
      {shown.map((r) => (
        <div key={r.id} className="nye-needs-row">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="nye-row nye-row--wrap" style={{ gap: 6, alignItems: 'center' }}>
              <span className="nye-strong">{r.customer_name || formatPhone(r.customer_phone) || 'Customer'}</span>
              <span className="nye-muted">· {r.service_name}</span>
              <NoShowTag count={r.customer_no_shows} />
            </div>
            <div className="nye-small nye-muted">
              {formatDateTime(r.starts_at)} · {r.staff_name || 'Any available stylist'}
            </div>
          </div>
          <div className="nye-row" style={{ gap: 8, flexShrink: 0 }}>
            <button type="button" className="nye-btn nye-btn--success-solid nye-btn--xs" disabled={busy === r.id} onClick={() => onMark(r, 'service_taken')}>
              ✅ Came
            </button>
            <button type="button" className="nye-btn nye-btn--danger-solid nye-btn--xs" disabled={busy === r.id} onClick={() => onMark(r, 'no_show')}>
              ❌ No-show
            </button>
          </div>
        </div>
      ))}
      {rows.length > 5 ? (
        <button type="button" className="nye-link-btn" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      ) : null}
    </div>
  );
}

function StatusSelect({ row, busy, onChange }) {
  return (
    <select
      className={`nye-status-select nye-status-select--${row.status}`}
      value={row.status}
      disabled={busy}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(row, e.target.value)}
      title="Change status"
      aria-label="Appointment status"
    >
      {Object.entries(STATUS_LABEL).map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

const CSV_COLUMNS = [
  { label: 'Customer Name', key: 'customer_name' },
  { label: 'Phone', key: 'customer_phone' },
  { label: 'Service', key: 'service_name' },
  { label: 'Assigned Staff', value: (r) => r.staff_name || 'Unassigned' },
  { label: 'Source', value: (r) => (r.source === 'sarvam' || r.source === 'voice' ? 'Voice call' : r.source === 'walk_in' ? 'Walk-in / desk' : r.source === 'instagram' ? 'Instagram' : 'WhatsApp') },
  { label: 'First Activity', key: 'first_activity_at' },
  { label: 'Date & Time', key: 'starts_at' },
  { label: 'Status', key: 'status' },
];

export default function Appointments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [stylist, setStylist] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [chatFor, setChatFor] = useState(null);
  const [chatChannel, setChatChannel] = useState('whatsapp');
  const [recording, setRecording] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [booking, setBooking] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [params, setParams] = useSearchParams();

  // Opened from the presentation page's Booking tile (?book=walk_in or ?book=later): open the form.
  useEffect(() => {
    const mode = params.get('book');
    if (mode === 'walk_in' || mode === 'later') {
      setBooking({ mode });
      params.delete('book');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return api
      .listAppointments()
      .then((data) => {
        setRows(data);
        setDetail((d) => (d ? data.find((r) => r.id === d.id) || null : d));
      })
      .catch((e) => toast.error(e.message || 'Failed to load appointments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (source === 'voice' && r.source !== 'voice' && r.source !== 'sarvam') return false;
      if (source && source !== 'voice' && r.source !== source) return false;
      if (stylist === 'assigned' && !r.staff_name) return false;
      if (stylist === 'unassigned' && r.staff_name) return false;
      if (!inDateRange(r.starts_at, dateFrom, dateTo)) return false;
      if (!q) return true;
      return [r.customer_name, r.customer_phone, r.service_name, r.staff_name].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, search, status, source, stylist, dateFrom, dateTo]);

  const { page, setPage, pageSize, setPageSize, paginated, totalPages, offset } = usePagination(filtered);
  const needsUpdate = useMemo(
    () => rows.filter((r) => r.needs_update).sort((a, b) => String(b.starts_at).localeCompare(String(a.starts_at))),
    [rows],
  );

  // Status change: ask first, because Service taken sends WhatsApp messages.
  const requestStatus = (row, next) => {
    if (!next || next === row.status) return;
    setPendingStatus({ row, next });
  };

  const applyStatus = async () => {
    const { row, next } = pendingStatus;
    setPendingStatus(null);
    setBusy(row.id);
    try {
      const res = await api.setAppointmentStatus(row.id, next);
      toast.success(`${row.customer_name || 'Appointment'}: ${STATUS_LABEL[next]}. ${(res.notes || []).join('. ')}`);
      await load(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const openConversation = (row) => {
    if (row.source === 'sarvam' || row.source === 'voice') {
      setRecording({ ...(row.call || {}), customer_name: row.customer_name, customer_phone: row.customer_phone, service_name: row.service_name, starts_at: row.starts_at, staff_name: row.staff_name });
    } else {
      setChatChannel(row.source === 'instagram' ? 'instagram' : 'whatsapp');
      setChatFor(row.customer_id);
    }
  };

  const doExport = () => {
    if (!filtered.length) {
      toast.error('No appointments to export');
      return;
    }
    exportCSV(filtered, CSV_COLUMNS, 'appointments');
    toast.success(`Exported ${filtered.length} appointments`);
  };

  return (
    <div className="nye-fade">
      <WelcomeBar title="Appointments" sub="Bookings from WhatsApp, voice calls and the desk. Any-stylist bookings wait here until you assign a stylist." />

      <div className="nye-panel">
        <div className="nye-toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, phone, service or stylist" />
          <button type="button" className="nye-chip" onClick={() => load(false)} title="Refresh">
            <span className="nye-row" style={{ gap: 6 }}>
              <RefreshIcon /> Refresh
            </span>
          </button>
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={doExport}>
            <DownloadIcon /> Excel
          </button>
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setBooking({ mode: 'later' })}>
            + Book appointment
          </button>
          <button type="button" className="nye-btn nye-btn--primary nye-btn--sm" onClick={() => setBooking({ mode: 'walk_in' })}>
            🚶 Walk-in
          </button>
        </div>

        <NeedsUpdate rows={needsUpdate} busy={busy} onMark={requestStatus} />

        <DateRange
          from={dateFrom}
          to={dateTo}
          onFrom={setDateFrom}
          onTo={setDateTo}
          onClear={() => {
            setDateFrom('');
            setDateTo('');
          }}
        />
        <ChipRow label="Status" chips={STATUS_CHIPS} value={status} onChange={setStatus} />
        <ChipRow label="Source" chips={SOURCE_CHIPS} value={source} onChange={setSource} />
        <ChipRow label="Stylist" chips={STYLIST_CHIPS} value={stylist} onChange={setStylist} />

        <div className="nye-count-line">
          {filtered.length} appointment{filtered.length === 1 ? '' : 's'}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="nye-table-shell">
            <div className="nye-table-wrap">
              <table className="nye-table">
                <thead>
                  <tr>
                    {['S.No', 'Customer Name', 'Phone', 'Service', 'Assigned Staff', 'Source', 'First Activity', 'Date & Time', 'Status', 'Action'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="nye-table-empty">
                        No appointments found
                      </td>
                    </tr>
                  ) : (
                    paginated.map((r, i) => (
                      <tr key={r.id} className="nye-row-click" onClick={() => setDetail(r)}>
                        <td className="nye-td-muted">{offset + i + 1}</td>
                        <td>
                          <div className="nye-td-strong">{r.customer_name || '—'}</div>
                          {r.contact_name && r.contact_name !== r.customer_name ? <div className="nye-td-muted">via {r.contact_name}</div> : null}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatPhone(r.customer_phone)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.service_name}</td>
                        <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => r.status === 'booked' && !r.staff_name && e.stopPropagation()}>
                          {r.staff_name ? (
                            r.staff_name
                          ) : r.status === 'booked' ? (
                            <button type="button" className="nye-assign-btn" onClick={() => setAssignFor(r.id)} title="Give this booking a stylist">
                              ⏳ Assign stylist
                            </button>
                          ) : (
                            <UnassignedPill />
                          )}
                        </td>
                        <td>
                          <SourceBadge source={r.source} />
                        </td>
                        <td className="nye-td-muted" style={{ minWidth: 110 }}>
                          {formatDateTime(r.first_activity_at)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.starts_at)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <StatusSelect row={r} busy={busy === r.id} onChange={requestStatus} />
                          {r.status === 'no_show' && r.no_show_auto ? <div className="nye-td-muted nye-small">Auto at closing</div> : null}
                          {r.needs_update ? <div className="nye-small" style={{ color: 'var(--nye-warn)' }}>Did they come?</div> : null}
                        </td>
                        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          <ViewButton onClick={() => openConversation(r)} title={r.source === 'sarvam' || r.source === 'voice' ? 'Play call recording' : r.source === 'instagram' ? 'View Instagram chat' : 'View WhatsApp chat'} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
          </div>
        )}
      </div>

      {detail ? (
        <Modal title="Appointment details" onClose={() => setDetail(null)} size="wide">
          <DetailRow label="Booked for">{detail.customer_name || '—'}</DetailRow>
          {detail.contact_name && detail.contact_name !== detail.customer_name ? (
            <DetailRow label="Contact">{detail.contact_name} (owner of this number)</DetailRow>
          ) : null}
          <DetailRow label="Phone">{formatPhone(detail.customer_phone)}</DetailRow>
          <DetailRow label="Service">{detail.service_name}</DetailRow>
          <DetailRow label="Assigned">
            <span className="nye-row" style={{ gap: 8, alignItems: 'center' }}>
              {detail.staff_name || <UnassignedPill />}
              {detail.status === 'booked' ? (
                <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={() => setAssignFor(detail.id)}>
                  {detail.staff_name ? 'Change stylist or time' : 'Assign stylist'}
                </button>
              ) : null}
            </span>
          </DetailRow>
          <DetailRow label="Source">
            <SourceBadge source={detail.source} showLabel />
          </DetailRow>
          <DetailRow label="First activity">{formatDateTime(detail.first_activity_at)}</DetailRow>
          <DetailRow label="Date & time">{formatDateTime(detail.starts_at)}</DetailRow>
          <DetailRow label="Status">
            <StatusSelect row={detail} busy={busy === detail.id} onChange={requestStatus} />
          </DetailRow>
          {detail.service_taken_at ? <DetailRow label="Service taken at">{formatDateTime(detail.service_taken_at)}</DetailRow> : null}
          {detail.no_show_at ? (
            <DetailRow label="Marked No-show">
              {formatDateTime(detail.no_show_at)}
              {detail.no_show_auto ? ' (automatically at closing time)' : ''}
            </DetailRow>
          ) : null}
          {detail.customer_no_shows >= 2 ? (
            <DetailRow label="History">
              <NoShowTag count={detail.customer_no_shows} /> Consider calling to confirm their next visit.
            </DetailRow>
          ) : null}
          <DetailRow label="Booked at">{formatDateTime(detail.created_at)}</DetailRow>

          <div className="nye-callout nye-mt-16">
            Change the status with the menu above. <strong>Service taken</strong> sends the feedback message and schedules the return-visit
            reminders. <strong>No-show</strong> sends the missed-appointment message, offering a new time. <strong>Booked</strong> undoes a mistake and schedules the reminders that are still
            ahead.
          </div>

          <div className="nye-row nye-row--wrap nye-mt-16" style={{ gap: 10 }}>
            <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => openConversation(detail)}>
              {detail.source === 'sarvam' || detail.source === 'voice' ? '▶ Play recording' : '👁 View chat'}
            </button>
            {detail.status !== 'service_taken' ? (
              <button
                type="button"
                className="nye-btn nye-btn--primary nye-btn--sm"
                disabled={busy === detail.id}
                onClick={() => requestStatus(detail, 'service_taken')}
              >
                {busy === detail.id ? <Spinner light /> : null}
                Mark service taken
              </button>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {pendingStatus ? (
        <Confirm
          title={`Change to ${STATUS_LABEL[pendingStatus.next]}?`}
          message={`${pendingStatus.row.customer_name || 'Customer'} · ${pendingStatus.row.service_name}: ${STATUS_LABEL[pendingStatus.row.status]} → ${
            STATUS_LABEL[pendingStatus.next]
          }. ${statusEffect(pendingStatus.row.status, pendingStatus.next)}`}
          confirmLabel={`Mark ${STATUS_LABEL[pendingStatus.next]}`}
          danger={pendingStatus.next === 'no_show'}
          onConfirm={applyStatus}
          onCancel={() => setPendingStatus(null)}
        />
      ) : null}

      {booking ? <BookingModal preset={booking} onClose={() => setBooking(null)} onBooked={() => load(true)} /> : null}
      {assignFor ? (
        <AssignStylistModal
          appointmentId={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => {
            setDetail(null);
            load(true);
          }}
        />
      ) : null}

      {chatFor ? <ChatModal customerId={chatFor} channel={chatChannel} onClose={() => setChatFor(null)} /> : null}
      {recording ? <RecordingModal call={recording} onClose={() => setRecording(null)} /> : null}
    </div>
  );
}
